import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import api, { asArray } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const heroImg = "https://images.pexels.com/photos/7256576/pexels-photo-7256576.jpeg";
const collectionImages = {
  "resin-art": "https://images.unsplash.com/photo-1615529182904-14819c35db37",
  "handmade-bags": "https://images.unsplash.com/photo-1473188588951-666fce8e7c68",
  "pearl-jewellery": "https://images.unsplash.com/photo-1595345705177-ffe090eb0784",
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    (async () => {
      const [featured, best, nw, t, g] = await Promise.all([
        api.get("/products?tag=featured&limit=8"),
        api.get("/products?tag=best-seller&limit=8"),
        api.get("/products?tag=new-arrival&limit=8"),
        api.get("/testimonials"),
        api.get("/gallery"),
      ]);
      setProducts(asArray(featured.data));
      setBestSellers(asArray(best.data));
      setNewArrivals(asArray(nw.data));
      setTestimonials(asArray(t.data));
      setGallery(asArray(g.data).slice(0, 6));
    })().catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Studio pour" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/10 via-transparent to-cream" />
        </div>
        <div className="relative w-full max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }} className="max-w-2xl">
            <div className="overline text-ivory/85 mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-gold" /> Winter Edit · MMXXVI
            </div>
            <h1 className="font-serif text-ivory text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight" data-testid="hero-heading">
              Objects made<br />
              slowly, held<br />
              <em className="text-gold not-italic font-normal">for a lifetime.</em>
            </h1>
            <p className="mt-8 text-ivory/85 max-w-lg text-[17px] leading-relaxed">
              Resin poured under linen shade, leather cut by two hands, pearls knotted on silk — each object one of very few in the world.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Link to="/shop" data-testid="hero-cta-shop" className="group inline-flex items-center gap-3 bg-emerald text-ivory px-8 py-4 rounded-none text-sm tracking-[0.15em] uppercase hover:bg-emerald/90 transition-colors">
                Enter the atelier <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/about" className="nav-link text-ivory text-sm tracking-[0.15em] uppercase">The Journal</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Collections */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="flex items-end justify-between mb-12 gap-8">
          <div>
            <div className="overline text-emerald mb-3">The Three Houses</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight max-w-xl">Three quiet obsessions, curated as one house.</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-block nav-link overline text-charcoal">All collections</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {[
            { slug: "resin-art", name: "Resin Art", tag: "Ocean & Emerald" },
            { slug: "handmade-bags", name: "Handmade Bags", tag: "Italian Leather" },
            { slug: "pearl-jewellery", name: "Pearl Jewellery", tag: "Freshwater · Silk-knotted" },
          ].map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: i * 0.1, ease: [0.2, 0.7, 0.2, 1] }}
              data-testid={`collection-${c.slug}`}
            >
              <Link to={`/category/${c.slug}`} className="block group">
                <div className="relative img-hover aspect-[3/4] bg-ivory">
                  <img src={collectionImages[c.slug]} alt={c.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="overline text-ivory/80">{c.tag}</div>
                    <div className="mt-2 font-serif text-3xl text-ivory tracking-tight flex items-center gap-3">
                      {c.name} <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Best sellers */}
      {bestSellers.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="overline text-emerald mb-3">Loved most</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Best sellers</h2>
            </div>
            <Link to="/shop?tag=best-seller" className="nav-link overline text-charcoal">View all</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-12">
            {bestSellers.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* Editorial split */}
      <section className="bg-ivory/70 grain relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
            <div className="overline text-emerald mb-4"><Sparkles className="w-3 h-3 inline mr-2" />Meet the artist</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight max-w-lg">
              A studio on the fourth floor, where light lands sideways by four.
            </h2>
            <p className="mt-8 text-charcoal/70 leading-relaxed max-w-lg text-[15px]">
              Crystal Clear Craft began as one woman's obsession with a single wave — how the sun caught it, how the green fell into the gold. Every object we make begins there, in patience with light. Nothing here is made twice the same way.
            </p>
            <Link to="/about" className="mt-10 inline-flex items-center gap-3 group">
              <span className="overline text-charcoal">Read the journal</span>
              <span className="w-8 h-px bg-charcoal group-hover:w-14 transition-all" />
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 1 }} className="grid grid-cols-2 gap-3">
            <img src="https://images.pexels.com/photos/7256576/pexels-photo-7256576.jpeg" className="w-full aspect-[3/4] object-cover" alt="Studio" />
            <img src="https://images.unsplash.com/photo-1615529182904-14819c35db37" className="w-full aspect-[3/4] object-cover mt-12" alt="Resin close-up" />
          </motion.div>
        </div>
      </section>

      {/* New arrivals */}
      {newArrivals.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="overline text-emerald mb-3">Just arrived</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight">New arrivals</h2>
            </div>
            <Link to="/shop?tag=new-arrival" className="nav-link overline text-charcoal">See all new</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-12">
            {newArrivals.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="bg-charcoal text-ivory py-24 lg:py-32 relative overflow-hidden">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="overline text-gold mb-3">Voices from the studio</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight max-w-2xl">What people write, once the linen is unwrapped.</h2>
            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10">
              {testimonials.slice(0, 3).map((t, i) => (
                <motion.figure
                  key={t.id || i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.12 }}
                >
                  <div className="text-gold text-2xl font-serif mb-4">"</div>
                  <blockquote className="font-serif text-2xl leading-snug text-ivory/95">{t.quote}</blockquote>
                  <figcaption className="mt-6 text-sm text-ivory/60">— {t.name}{t.role ? `, ${t.role}` : ""}</figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery preview */}
      {gallery.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="overline text-emerald mb-3">Instagram-style</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Studio journal</h2>
            </div>
            <Link to="/gallery" className="nav-link overline text-charcoal">Enter gallery</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {gallery.map((g, i) => (
              <motion.div key={g.id || i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: (i % 6) * 0.05 }} className={`img-hover aspect-square ${i % 5 === 0 ? "md:row-span-2 md:aspect-[3/4] lg:aspect-[3/4]" : ""}`}>
                <img src={g.url} alt={g.caption} className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
